import * as pdfjsLib from "pdfjs-dist";
import { OPS } from "pdfjs-dist";
import type { PatternAnnotation, PatternHole, PatternZoneLine } from "@/types/pattern";

// Use the bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).toString();

interface TextItem {
  str: string;
  x: number;
  y: number;
  fontSize: number;
  rotation: number; // degrees, 0 = horizontal
}

/** Known hole-ID patterns from SHOTPlus CSVs */
const HOLE_ID_RE = /^(?:B|F|T)?\d{1,3}$/i;

/** Matches depth labels like "12.0m", "11.9m" */
const DEPTH_RE = /^\d{1,3}\.\d{1}m?$/;

/** Short numeric-only strings */
const NUMERIC_JUNK_RE = /^[\d.,\s]+$/;

/** SHOTPlus boilerplate */
const BOILERPLATE_RE = /^(SHOTPlus|Mine|Location|Title|Filename|Scale|Not to scale|Author)/i;

/** Anchor: a hole ID found in the PDF with its PDF-space position */
interface PdfAnchor {
  holeId: string;
  pdfX: number;
  pdfY: number;
}

/**
 * Parse a SHOTPlus drill map PDF and extract annotations, anchor points, and ore zone lines.
 * Pass in the parsed CSV holes so we can map PDF coordinates → easting/northing.
 */
export async function parsePatternPDF(
  data: ArrayBuffer,
  csvHoles?: PatternHole[],
): Promise<{ annotations: PatternAnnotation[]; zoneLines: PatternZoneLine[]; errors: string[] }> {
  const errors: string[] = [];
  const annotations: PatternAnnotation[] = [];
  const zoneLines: PatternZoneLine[] = [];
  const anchors: PdfAnchor[] = [];

  try {
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    const page = await pdf.getPage(1);
    const textContent = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1 });

    // Extract all text items with position and font size
    const items: TextItem[] = [];
    for (const item of textContent.items) {
      if (!("str" in item) || !item.str.trim()) continue;
      const tx = item.transform;
      // tx = [a, b, c, d, e, f] — standard 2D affine matrix
      // rotation = atan2(b, a), font size from scale components
      const rotRad = Math.atan2(tx[1], tx[0]);
      const rotDeg = rotRad * (180 / Math.PI);
      items.push({
        str: item.str.trim(),
        x: tx[4],
        y: tx[5],
        fontSize: Math.abs(tx[0]) || Math.abs(tx[3]) || 12,
        rotation: Math.abs(rotDeg) < 0.5 ? 0 : Math.round(rotDeg * 10) / 10,
      });
    }

    // Newer SHOTPlus exports rasterise the whole page (no text layer). In that
    // case items.length === 0; we still try the pink-pixel extraction below so
    // ore boundaries can come from the raster. Fall through instead of early
    // return so the caller sees a useful error and any extracted zoneLines.
    if (items.length === 0) {
      errors.push(
        "PDF has no selectable text (rasterised export). Hole-ID anchors and drilling notes can't be scraped; upload a DXF for the ore body instead.",
      );
    } else {
      // Compute most common font size (used by hole IDs and depths)
      const sizes = items.map((i) => Math.round(i.fontSize * 10) / 10);
      const sizeFreq = new Map<number, number>();
      for (const s of sizes) sizeFreq.set(s, (sizeFreq.get(s) ?? 0) + 1);
      const medianSize = [...sizeFreq.entries()].sort((a, b) => b[1] - a[1])[0][0];

      const summaryXThreshold = viewport.width * 0.7;

      // First pass: collect hole ID anchors from individual text items (before grouping)
      for (const item of items) {
        if (HOLE_ID_RE.test(item.str)) {
          anchors.push({ holeId: item.str, pdfX: item.x, pdfY: item.y });
        }
      }

      // Group items into lines for annotation extraction
      const lines = groupIntoLines(items);

      for (const line of lines) {
        const text = line.text;
        const { x, y, fontSize, rotation } = line;

        if (text.length < 2) continue;
        if (HOLE_ID_RE.test(text)) continue;
        if (DEPTH_RE.test(text)) continue;
        if (NUMERIC_JUNK_RE.test(text)) continue;
        if (BOILERPLATE_RE.test(text)) continue;
        if (/^[NSEW]$/.test(text) || /^[XYZ]$/.test(text)) continue;

        const rot = rotation !== 0 ? rotation : undefined;

        if (x > summaryXThreshold && fontSize <= medianSize * 1.5) {
          annotations.push({ text, type: "summary", x, y, rotation: rot });
        } else if (fontSize > medianSize * 1.3 || text.length > 20) {
          annotations.push({ text, type: "instruction", x, y, rotation: rot });
        } else {
          annotations.push({ text, type: "zone", x, y, rotation: rot });
        }
      }

      // Build coordinate transform from anchors
      let transform: AffineTransform | null = null;
      if (csvHoles && csvHoles.length > 0 && anchors.length >= 2) {
        transform = computePdfToPatternTransform(anchors, csvHoles);
        if (transform) {
          for (const ann of annotations) {
            if (ann.x != null && ann.y != null) {
              const mapped = applyTransform(transform, ann.x, ann.y);
              ann.easting = mapped.easting;
              ann.northing = mapped.northing;
              // Negate rotation: PDF Y-up → SVG Y-down flips rotation direction
              if (ann.rotation) ann.rotation = -ann.rotation;
            }
          }
        }
      }
    }

    // Extract ore zone boundary lines from pink pixels in the raster image tiles.
    // Works on both text-layer and raster-only PDFs — runs regardless of items.length.
    if (csvHoles && csvHoles.length > 0) {
      try {
        const extracted = await extractPinkLines(page, csvHoles);
        zoneLines.push(...extracted);
      } catch (err) {
        errors.push(`Pink line extraction: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  } catch (err) {
    errors.push(`PDF parse error: ${err instanceof Error ? err.message : String(err)}`);
  }

  return { annotations, zoneLines, errors };
}

/**
 * Affine transform: maps PDF (x, y) → pattern (easting, northing).
 * Accounts for scale, translation, and Y-axis flip.
 */
interface AffineTransform {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Build a transform from PDF coordinates → easting/northing using matched anchors.
 * Uses least-squares fit over all matched hole IDs.
 */
function computePdfToPatternTransform(
  anchors: PdfAnchor[],
  csvHoles: PatternHole[],
): AffineTransform | null {
  // Build a lookup from hole ID → CSV position
  const holeMap = new Map<string, PatternHole>();
  for (const h of csvHoles) holeMap.set(h.holeId, h);

  // Match anchors to CSV holes
  const matched: { pdfX: number; pdfY: number; easting: number; northing: number }[] = [];
  for (const a of anchors) {
    const h = holeMap.get(a.holeId);
    if (h) {
      matched.push({ pdfX: a.pdfX, pdfY: a.pdfY, easting: h.collarEasting, northing: h.collarNorthing });
    }
  }

  if (matched.length < 2) return null;

  // Compute linear regression for easting = scaleX * pdfX + offsetX
  // and northing = scaleY * pdfY + offsetY
  const n = matched.length;
  let sumPx = 0, sumPy = 0, sumE = 0, sumN = 0;
  let sumPxPx = 0, sumPyPy = 0, sumPxE = 0, sumPyN = 0;

  for (const m of matched) {
    sumPx += m.pdfX;
    sumPy += m.pdfY;
    sumE += m.easting;
    sumN += m.northing;
    sumPxPx += m.pdfX * m.pdfX;
    sumPyPy += m.pdfY * m.pdfY;
    sumPxE += m.pdfX * m.easting;
    sumPyN += m.pdfY * m.northing;
  }

  const denomX = n * sumPxPx - sumPx * sumPx;
  const denomY = n * sumPyPy - sumPy * sumPy;

  if (Math.abs(denomX) < 1e-10 || Math.abs(denomY) < 1e-10) return null;

  const scaleX = (n * sumPxE - sumPx * sumE) / denomX;
  const offsetX = (sumE - scaleX * sumPx) / n;
  const scaleY = (n * sumPyN - sumPy * sumN) / denomY;
  const offsetY = (sumN - scaleY * sumPy) / n;

  return { scaleX, scaleY, offsetX, offsetY };
}

function applyTransform(
  t: AffineTransform,
  pdfX: number,
  pdfY: number,
): { easting: number; northing: number } {
  return {
    easting: t.scaleX * pdfX + t.offsetX,
    northing: t.scaleY * pdfY + t.offsetY,
  };
}

interface LineBucket {
  text: string;
  x: number;
  y: number;
  fontSize: number;
  rotation: number;
}

function groupIntoLines(items: TextItem[]): LineBucket[] {
  if (items.length === 0) return [];

  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);

  const lines: LineBucket[] = [];
  let current: TextItem[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = current[0];
    const item = sorted[i];
    if (Math.abs(item.y - prev.y) < prev.fontSize * 0.6) {
      current.push(item);
    } else {
      lines.push(mergeLine(current));
      current = [item];
    }
  }
  lines.push(mergeLine(current));

  return lines;
}

function mergeLine(items: TextItem[]): LineBucket {
  items.sort((a, b) => a.x - b.x);
  const text = items.map((i) => i.str).join(" ");
  const maxFontItem = items.reduce((a, b) => (b.fontSize > a.fontSize ? b : a));
  // Use the dominant rotation from the line's items (most common non-zero, or 0)
  const rotations = items.map((i) => i.rotation).filter((r) => r !== 0);
  const rotation = rotations.length > 0 ? rotations[0] : 0;
  return {
    text,
    x: items[0].x,
    y: items[0].y,
    fontSize: maxFontItem.fontSize,
    rotation,
  };
}

// ─── Pink pixel image analysis for ore boundary lines ───

interface TileInfo {
  name: string;
  transform: number[];
}

interface ComponentStats {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  count: number;
  pixels: { x: number; y: number }[];
}

/**
 * Extract ore zone boundary lines by analysing pink pixels in the PDF's raster tiles.
 * SHOTPlus drill maps are tiled raster images; ore boundaries are drawn in pink (R>200, G~127, B>200).
 * Uses connected component analysis to find elongated line features, then maps them to easting/northing.
 */
async function extractPinkLines(
  page: pdfjsLib.PDFPageProxy,
  csvHoles: PatternHole[],
): Promise<PatternZoneLine[]> {
  const ops = await page.getOperatorList();

  // Collect image tile transforms
  let ct: number[] | null = null;
  const tiles: TileInfo[] = [];
  for (let i = 0; i < ops.fnArray.length; i++) {
    if (ops.fnArray[i] === OPS.transform) ct = ops.argsArray[i] as number[];
    if (ops.fnArray[i] === OPS.paintImageXObject && ct) {
      tiles.push({ name: (ops.argsArray[i] as string[])[0], transform: [...ct] });
    }
  }

  if (tiles.length === 0) return [];

  // Get tile dimensions from first image
  const firstImg = await new Promise<{ width: number; height: number; data: Uint8ClampedArray }>((resolve) =>
    page.objs.get(tiles[0].name, resolve),
  );
  const totalWidth = firstImg.width;

  // Downsample factor to reduce memory usage (2x = ~4M pixels instead of ~16M)
  const DS = 2;
  const dsW = Math.ceil(totalWidth / DS);

  // Process tiles and build downsampled pink pixel mask
  let totalDsHeight = 0;
  const tileHeights: number[] = [];

  // First pass: get all tile heights
  for (let t = 0; t < tiles.length; t++) {
    const img = await new Promise<{ width: number; height: number; data: Uint8ClampedArray }>((resolve) =>
      page.objs.get(tiles[t].name, resolve),
    );
    tileHeights.push(img.height);
    totalDsHeight += Math.ceil(img.height / DS);
  }

  const mask = new Uint8Array(dsW * totalDsHeight);
  let dsYOff = 0;

  for (let t = 0; t < tiles.length; t++) {
    const img = await new Promise<{ width: number; height: number; data: Uint8ClampedArray }>((resolve) =>
      page.objs.get(tiles[t].name, resolve),
    );
    const { width, height, data: px } = img;
    const dsH = Math.ceil(height / DS);

    for (let dy = 0; dy < dsH; dy++) {
      for (let dx = 0; dx < dsW; dx++) {
        // Sample from original resolution
        const sx = Math.min(dx * DS, width - 1);
        const sy = Math.min(dy * DS, height - 1);
        const idx = (sy * width + sx) * 4;
        const r = px[idx], g = px[idx + 1], b = px[idx + 2];
        // Pink: R>200, G around 80-180, B>200
        if (r > 200 && b > 200 && g > 80 && g < 180) {
          mask[(dsYOff + dy) * dsW + dx] = 1;
        }
      }
    }
    dsYOff += dsH;
  }

  // Connected component labelling (8-connected)
  const labels = new Int32Array(dsW * totalDsHeight);
  let nextLabel = 1;
  const parent = [0];

  function find(x: number): number {
    while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
    return x;
  }
  function union(a: number, b: number) {
    a = find(a); b = find(b);
    if (a !== b) parent[Math.max(a, b)] = Math.min(a, b);
  }

  for (let y = 0; y < totalDsHeight; y++) {
    for (let x = 0; x < dsW; x++) {
      const idx = y * dsW + x;
      if (!mask[idx]) continue;

      const neighbours: number[] = [];
      if (x > 0) neighbours.push(labels[idx - 1]);
      if (y > 0) neighbours.push(labels[idx - dsW]);
      if (y > 0 && x > 0) neighbours.push(labels[idx - dsW - 1]);
      if (y > 0 && x < dsW - 1) neighbours.push(labels[idx - dsW + 1]);

      const valid = neighbours.filter((l) => l > 0);
      if (valid.length === 0) {
        labels[idx] = nextLabel;
        parent.push(nextLabel);
        nextLabel++;
      } else {
        const min = Math.min(...valid);
        labels[idx] = min;
        for (const v of valid) if (v !== min) union(v, min);
      }
    }
  }

  // Flatten labels
  for (let i = 0; i < labels.length; i++) if (labels[i]) labels[i] = find(labels[i]);

  // Collect component stats
  const comps = new Map<number, ComponentStats>();
  for (let y = 0; y < totalDsHeight; y++) {
    for (let x = 0; x < dsW; x++) {
      const l = labels[y * dsW + x];
      if (!l) continue;
      if (!comps.has(l)) comps.set(l, { minX: x, maxX: x, minY: y, maxY: y, count: 0, pixels: [] });
      const c = comps.get(l)!;
      c.minX = Math.min(c.minX, x);
      c.maxX = Math.max(c.maxX, x);
      c.minY = Math.min(c.minY, y);
      c.maxY = Math.max(c.maxY, y);
      c.count++;
      if (c.count <= 2000) c.pixels.push({ x, y });
    }
  }

  // Classify: elongated components with low density = ore boundary lines
  const lineComponents: ComponentStats[] = [];
  for (const c of comps.values()) {
    if (c.count < 3) continue; // noise (lower threshold since downsampled)
    const w = c.maxX - c.minX + 1;
    const h = c.maxY - c.minY + 1;
    const aspect = Math.max(w, h) / Math.min(w, h);
    const density = c.count / (w * h);
    const span = Math.max(w, h);

    if (span > 50 && density < 0.05 && aspect > 5) {
      lineComponents.push(c);
    }
  }

  if (lineComponents.length === 0) return [];

  // Build coordinate mapping from image space → easting/northing
  // Use the bounding box of hole positions in both coordinate systems
  const holeMap = new Map<string, PatternHole>();
  for (const h of csvHoles) holeMap.set(h.holeId, h);

  // Get easting/northing extents from CSV
  let cMinE = Infinity, cMaxE = -Infinity, cMinN = Infinity, cMaxN = -Infinity;
  for (const h of csvHoles) {
    if (h.collarEasting < cMinE) cMinE = h.collarEasting;
    if (h.collarEasting > cMaxE) cMaxE = h.collarEasting;
    if (h.collarNorthing < cMinN) cMinN = h.collarNorthing;
    if (h.collarNorthing > cMaxN) cMaxN = h.collarNorthing;
  }

  // Use the extent of line components to determine image-space mapping
  // The lines should span a similar extent to the drill holes
  let imgMinX = Infinity, imgMaxX = -Infinity, imgMinY = Infinity, imgMaxY = -Infinity;
  for (const c of lineComponents) {
    imgMinX = Math.min(imgMinX, c.minX);
    imgMaxX = Math.max(imgMaxX, c.maxX);
    imgMinY = Math.min(imgMinY, c.minY);
    imgMaxY = Math.max(imgMaxY, c.maxY);
  }

  // Also include all hole marker positions for better extent calculation
  // Find cyan pixels for hole markers (compact, high-density blobs)
  let markerMinX = Infinity, markerMaxX = -Infinity, markerMinY = Infinity, markerMaxY = -Infinity;
  for (const c of comps.values()) {
    const w = c.maxX - c.minX + 1;
    const h = c.maxY - c.minY + 1;
    const aspect = Math.max(w, h) / Math.min(w, h);
    const density = c.count / (w * h);
    // Compact blobs that could be hole markers
    if (c.count >= 3 && c.count <= 200 && aspect < 3 && density > 0.2) {
      const cx = (c.minX + c.maxX) / 2;
      const cy = (c.minY + c.maxY) / 2;
      markerMinX = Math.min(markerMinX, cx);
      markerMaxX = Math.max(markerMaxX, cx);
      markerMinY = Math.min(markerMinY, cy);
      markerMaxY = Math.max(markerMaxY, cy);
    }
  }

  // Use marker extent if available, otherwise use line extent
  const refMinX = isFinite(markerMinX) ? markerMinX : imgMinX;
  const refMaxX = isFinite(markerMaxX) ? markerMaxX : imgMaxX;
  const refMinY = isFinite(markerMinY) ? markerMinY : imgMinY;
  const refMaxY = isFinite(markerMaxY) ? markerMaxY : imgMaxY;

  if (refMaxX <= refMinX || refMaxY <= refMinY) return [];

  // Map: image X → easting (linear), image Y → northing (inverted — Y increases downward)
  const sX = (cMaxE - cMinE) / (refMaxX - refMinX);
  const oX = cMinE - refMinX * sX;
  const sY = (cMinN - cMaxN) / (refMaxY - refMinY);
  const oY = cMaxN - refMinY * sY;

  function toEN(imgX: number, imgY: number) {
    return { easting: sX * imgX + oX, northing: sY * imgY + oY };
  }

  // Convert line components to zone line polylines
  const result: PatternZoneLine[] = [];
  for (const c of lineComponents) {
    const pts = c.pixels;
    if (pts.length < 2) continue;

    // Sort by Y for vertical/diagonal lines
    pts.sort((a, b) => a.y - b.y);

    // Sample ~15 evenly spaced points along the line
    const step = Math.max(1, Math.floor(pts.length / 15));
    const points: { easting: number; northing: number }[] = [];
    for (let i = 0; i < pts.length; i += step) {
      points.push(toEN(pts[i].x, pts[i].y));
    }
    // Always include last point
    const last = pts[pts.length - 1];
    const lastEN = toEN(last.x, last.y);
    if (points.length === 0 || Math.abs(points[points.length - 1].northing - lastEN.northing) > 0.1) {
      points.push(lastEN);
    }

    // Sort by northing descending (north to south)
    points.sort((a, b) => b.northing - a.northing);

    result.push({ label: "ORE", color: "#ff80ff", points });
  }

  return result;
}
